import type { CSSProperties } from "react";
import { SectionBadge, StarIcon } from "./SitePrimitives";

type Testimonial = { quote: string; name: string; role?: string; rating: string; avatar?: string };

function quoted(text: string) {
  const trimmed = text.trim().replace(/^["“]\s*|\s*["”]$/g, "");
  return `"${trimmed}"`;
}

function TestimonialCard({ item }: { item: Testimonial }) {
  return (
    <article className="mb-6 rounded-xl bg-[#151E1E] p-5 shadow-[0_4px_9px_rgba(0,0,0,0.07)] min-[1200px]:mb-6">
      <p className="site-body text-[#9F9F9F]">{quoted(item.quote)}</p>
      <div className="mt-[26px]">
        <p className="site-body text-[#FAFAFA] min-[1200px]:text-[15px]">{item.name}</p>
        <p className="site-body mt-[3px] flex items-center gap-[5px] text-[#A3A3A3] min-[1200px]:text-[15px]">
          <span className="text-[#FAFAFA]">{item.rating || "5"}</span>
          <StarIcon />
          {item.role ? <span>{item.role}</span> : null}
        </p>
      </div>
    </article>
  );
}

function Column({ items, reverse, duration }: { items: Testimonial[]; reverse?: boolean; duration: number }) {
  return (
    <div className="site-marquee-host min-w-0 flex-1 overflow-hidden">
      <div
        className={`site-marquee-y ${reverse ? "site-marquee-reverse" : ""}`}
        style={{ "--marquee-duration": `${duration}s` } as CSSProperties}
      >
        <div>
          {items.map((item, index) => (
            <TestimonialCard key={`${item.name}-${index}`} item={item} />
          ))}
        </div>
        <div aria-hidden="true">
          {items.map((item, index) => (
            <TestimonialCard key={`${item.name}-${index}-copy`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function SiteTestimonials({
  badge,
  title,
  description,
  items,
}: {
  badge: string;
  title: string;
  description?: string;
  items: Testimonial[];
}) {
  if (!items.length) return null;
  const half = Math.ceil(items.length / 2);
  const left = items.slice(0, half);
  const right = items.slice(half);
  const fade = "linear-gradient(180deg, transparent 0%, #000 26%, #000 74%, transparent 100%)";
  return (
    <section id="testimonials" className="pt-[106px] min-[810px]:pt-[114px] min-[1200px]:pt-[171px]">
      <div className="site-container flex flex-col gap-8 min-[810px]:flex-row min-[810px]:items-center min-[810px]:justify-between min-[810px]:gap-10 min-[1200px]:gap-20">
        <div className="min-[810px]:w-[293px] min-[810px]:shrink-0 min-[1200px]:w-[396px]">
          <SectionBadge>{badge}</SectionBadge>
          <h2 className="site-h2 mt-2 text-white min-[810px]:mt-[11px]">{title}</h2>
          {description ? <p className="site-lead mt-[63px] min-[1200px]:mt-[66px]">{description}</p> : null}
        </div>
        <div
          className="flex h-[806px] gap-6 overflow-hidden min-[810px]:h-[695px] min-[810px]:flex-1 min-[1200px]:w-[720px] min-[1200px]:flex-none"
          style={{ maskImage: fade, WebkitMaskImage: fade }}
        >
          <div className="flex min-w-0 flex-1 min-[1200px]:hidden">
            <Column items={items} duration={items.length * 9} />
          </div>
          <div className="hidden min-w-0 flex-1 gap-6 min-[1200px]:flex">
            <Column items={left} duration={left.length * 11} />
            <Column items={right.length ? right : left} reverse duration={(right.length || left.length) * 11} />
          </div>
        </div>
      </div>
    </section>
  );
}
